// components/ui/CouponInput.tsx
import React, { useState } from 'react';
import { ModernButton } from './ModernButton.tsx';

interface CouponInputProps {
  onApplyCoupon: (code: string) => Promise<void>;
  onRemoveCoupon: () => void;
  appliedCoupon: any;
  loading: boolean;
  disabled?: boolean;
}

export const CouponInput: React.FC<CouponInputProps> = ({
  onApplyCoupon,
  onRemoveCoupon,
  appliedCoupon,
  loading,
  disabled = false
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  const handleApply = async () => {
    if (!couponCode.trim()) return;
    
    setIsApplying(true);
    try {
      await onApplyCoupon(couponCode.trim());
      setCouponCode('');
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsApplying(false);
    }
  };

  const handleRemove = () => {
    onRemoveCoupon();
    setCouponCode('');
  };

  if (appliedCoupon) {
    return (
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center">
              <div className="w-5 h-5 text-green-500 ml-2">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-green-800 font-medium">
                كوبون مطبق: {appliedCoupon.code}
              </span>
            </div>
            <p className="text-sm text-green-600 mt-1">
              خصم {appliedCoupon.discount_type === 'percentage' 
                ? `${appliedCoupon.discount_value}%` 
                : `${appliedCoupon.discount_value} KWD`} 
              - وفرت {appliedCoupon.discount_amount} KWD
            </p>
          </div>
          <ModernButton
            variant="outline"
            size="sm"
            onClick={handleRemove}
            disabled={disabled}
          >
            إزالة
          </ModernButton>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        كود الخصم (اختياري)
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          placeholder="أدخل كود الخصم"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-mono"
          disabled={disabled || loading}
          maxLength={20}
        />
        <ModernButton
          onClick={handleApply}
          disabled={!couponCode.trim() || disabled || loading}
          loading={isApplying}
          size="sm"
        >
          تطبيق
        </ModernButton>
      </div>
    </div>
  );
};

// types/index.ts - Add these interfaces to your existing types file
export interface Coupon {
  code: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  discount_amount: number;
}

export interface PriceCalculation {
  total_price: number;
  original_price: number;
  discount_amount: number;
  coupon: Coupon | null;
  can_afford: boolean;
  payment_required: number;
  client_balance: {
    available_balance: number;
    available_credit: number;
    total_available: number;
    is_trusted: boolean;
  };
  payment_options: PaymentOption[];
  selected_deliverables: string[];
  plan: {
    id: number;
    title: string;
    type: string;
  };
}

export interface PaymentOption {
  name: string;
  label: string;
  use_balance: number;
  use_credit: number;
  payment_needed: number;
  recommended: boolean;
}
